"use client";

import { useEffect, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { UNISWAP_API_PATH } from "./constants";

interface PriceChartProps {
  poolAddress: string;
  token0Symbol: string;
}

interface ChartData {
  timestamp: number;
  date: string;
  price: number;
}

interface HourData {
  token0Price: string;
  token1Price: string;
  periodStartUnix: number;
}

export default function PriceChart({ poolAddress: poolId, token0Symbol }: PriceChartProps) {
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCurrent = true; // <-- flag to prevent race condition

    async function loadData() {
      try {
        setIsLoading(true);
        const response = await fetch(`${UNISWAP_API_PATH}?limit=48&pool=${poolId}`);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP error! Status: ${response.status}`);
        }

        if (!isCurrent) return;

        const data = await response.json();

        // Process the data for the chart
        const processedData = data.map((hourData: HourData) => {
          const price = Number.parseFloat(hourData.token0Price);

          // Format the timestamp for display
          const date = new Date(hourData.periodStartUnix * 1000);
          const formattedDate = `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}`;

          return {
            timestamp: hourData.periodStartUnix,
            date: formattedDate,
            price: price,
          };
        });

        setChartData(processedData);
      } catch (err) {
        console.error("Failed to fetch price data:", err);
        setError("Failed to load price data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
    return () => {
      isCurrent = false; // mark this effect as stale
    };
  }, [poolId]);

  if (isLoading) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error) {
    return <div className="h-[400px] flex items-center justify-center text-red-500">{error}</div>;
  }

  if (chartData.length === 0) {
    return <div className="h-[400px] flex items-center justify-center">No price data available for this token.</div>;
  }

  return (
    <div className={"h-[250px] sm:h-[300px] md:h-[350px]"}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{
            top: 5,
            right: 10,
            left: 0,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis
            dataKey="date"
            tick={{ fill: "#ccc", fontSize: "10px" }}
            tickMargin={8}
            tickFormatter={(value) => {
              // Extract just the time part for cleaner display
              return value.split(" ")[1];
            }}
            height={30}
          />
          <YAxis
            tick={{ fill: "#ccc", fontSize: "10px" }}
            tickMargin={5}
            domain={["auto", "auto"]}
            tickFormatter={(value) => {
              // Format based on value size to prevent cutoff
              if (value < 0.0001) return `${value.toFixed(9)}`;
              if (value < 0.01) return `${value.toFixed(6)}`;
              if (value < 1) return `${value.toFixed(4)}`;
              if (value > 1000000) return `${(value / 1000000).toFixed(2)}M`;
              if (value > 1000) return `${(value / 1000).toFixed(2)}K`;
              return `${value.toFixed(2)}`;
            }}
            tickSize={1}
            width={70}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#1e2430",
              border: "none",
              borderRadius: "4px",
              fontSize: "12px",
              padding: "8px",
            }}
            formatter={(value: number) => {
              // Format tooltip value based on size
              if (value < 0.0001) return [`${value.toExponential(4)}`, "Price"];
              if (value > 1000000) return [`${value.toLocaleString()}`, "Price"];
              return [`${value.toFixed(6)}`, "Price"];
            }}
            labelFormatter={(label) => `Time: ${label}`}
          />
          <Legend wrapperStyle={{ fontSize: "11px" }} />
          <Line
            type="monotone"
            dataKey="price"
            stroke="#4ade80" // Green color for price line
            activeDot={{ r: 8 }}
            name={`${token0Symbol} Price`}
            strokeWidth={2}
            dot={false} // Hide dots for cleaner look
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
